import type { CoachingPartnershipStatus } from '@efdigital/ll-addon-data';
import type { CoachingPlanner } from '@efdigital/ll-xceed-v2/utils/coaching/coachee/coaching-plan-manager';
import { array } from '@ember/helper';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import BsDropdown from 'ember-bootstrap/components/bs-dropdown';

interface CoachPartnershipActionsSignature {
  Args: {
    coachUserId: string;
    planner: CoachingPlanner;
    buttonClass: string;
    collapseMultipleActions?: boolean;
  };
}

export class CoachPartnershipActions extends Component<CoachPartnershipActionsSignature> {
  @tracked loading = false;

  @cached
  get canTransitionToPending() {
    return (
      this.args.planner.canTransitionToStatus(this.args.coachUserId, 'pending')
        .outcome === 'allowed'
    );
  }

  @cached
  get canTransitionToChemistry() {
    return (
      this.args.planner.canTransitionToStatus(
        this.args.coachUserId,
        'chemistry'
      ).outcome === 'allowed'
    );
  }

  @cached
  get canTransitionToChosen() {
    return (
      this.args.planner.canTransitionToStatus(this.args.coachUserId, 'chosen')
        .outcome === 'allowed'
    );
  }

  @cached
  get canTransitionToDeclined() {
    return false;
    /*
    TODO: Implement declined status - this is supported but we don't want to make it too easy to decline a coach.
    Might need to do it through an admin request?
    return (
      this.args.planner.canTransitionToStatus(
        this.args.coachUserId,
        'declined'
      ).outcome === 'allowed'
    );
    */
  }

  @cached
  get canTransitionToAny() {
    return (
      this.canTransitionToPending ||
      this.canTransitionToChemistry ||
      this.canTransitionToChosen ||
      this.canTransitionToDeclined
    );
  }

  @cached
  get canBookChemistrySession() {
    return this.args.planner.canBookChemistrySession(this.args.coachUserId);
  }

  @cached
  get hasScheduledChemistrySession() {
    return (
      this.args.planner
        .getChemistrySessions(this.args.coachUserId)
        .filter((session) => session.status === 'scheduled').length > 0
    );
  }

  get showBookChemistrySessionButton() {
    return this.canBookChemistrySession || this.hasScheduledChemistrySession;
  }

  setStatus = async (status: CoachingPartnershipStatus) => {
    this.loading = true;
    try {
      await this.args.planner.setStatusForCoach(this.args.coachUserId, status);
    } finally {
      this.loading = false;
    }
  };

  get hasMultipleActions() {
    let count = 0;
    if (this.showBookChemistrySessionButton) {
      count++;
    }
    if (this.canTransitionToPending) {
      count++;
    }
    if (this.canTransitionToChemistry) {
      count++;
    }
    if (this.canTransitionToChosen) {
      count++;
    }
    if (this.canTransitionToDeclined) {
      count++;
    }
    return count > 1;
  }

  get useDropdown() {
    return this.args.collapseMultipleActions && this.hasMultipleActions;
  }

  <template>
    {{#if this.useDropdown}}
      <BsDropdown as |ddm|>
        <ddm.button class="{{@buttonClass}} btn-primary">
          Actions
        </ddm.button>
        <ddm.menu as |menu|>
          {{#if this.showBookChemistrySessionButton}}
            <menu.item>
              <menu.linkTo
                @route="coaching.coachee.cohort.coaches.coach.book-chemistry"
                @models={{array @coachUserId}}
              >
                {{#if this.hasScheduledChemistrySession}}
                  Chemistry session
                {{else}}
                  Book chemistry session
                {{/if}}
                <iconify-icon icon="ph:arrow-right-bold" inline />
              </menu.linkTo>
            </menu.item>
          {{/if}}
          {{#if this.canTransitionToPending}}
            <menu.item>
              <button
                {{on "click" (fn this.setStatus "pending")}}
                type="button"
                class="dropdown-item"
                disabled={{this.loading}}
              >
                <iconify-icon icon="ph:minus-bold" inline />
                Remove from shortlist
              </button>
            </menu.item>
          {{/if}}
          {{#if this.canTransitionToChemistry}}
            <menu.item>
              <button
                {{on "click" (fn this.setStatus "chemistry")}}
                type="button"
                class="dropdown-item"
                disabled={{this.loading}}
              >
                <iconify-icon icon="ph:plus-bold" inline />
                Add to shortlist
              </button>
            </menu.item>
          {{/if}}
          {{#if this.canTransitionToChosen}}
            <menu.item>
              <button
                {{on "click" (fn this.setStatus "chosen")}}
                type="button"
                class="dropdown-item"
                disabled={{this.loading}}
              >
                <iconify-icon icon="ph:check-bold" inline />
                Choose coach
              </button>
            </menu.item>
          {{/if}}
          {{#if this.canTransitionToDeclined}}
            <menu.item>
              <button
                type="button"
                {{on "click" (fn this.setStatus "declined")}}
                class="dropdown-item"
                disabled={{this.loading}}
              >
                <iconify-icon icon="ph:x-bold" inline />
                Reject coach
              </button>
            </menu.item>
          {{/if}}
        </ddm.menu>
      </BsDropdown>
    {{else}}
      {{#if this.showBookChemistrySessionButton}}
        <LinkTo
          @route="coaching.coachee.cohort.coaches.coach.book-chemistry"
          @models={{array @coachUserId}}
          class="{{@buttonClass}}
            btn-primary
            {{if this.canTransitionToAny 'me-3'}}"
        >
          {{#if this.hasScheduledChemistrySession}}
            Chemistry session
          {{else}}
            Book chemistry session
          {{/if}}
          <iconify-icon icon="ph:arrow-right-bold" inline />
        </LinkTo>
      {{/if}}
      {{#if this.canTransitionToPending}}
        <button
          class="{{@buttonClass}} btn-primary"
          type="button"
          {{on "click" (fn this.setStatus "pending")}}
          disabled={{this.loading}}
        >
          <iconify-icon icon="ph:minus-bold" inline />
          Remove from shortlist
        </button>
      {{/if}}
      {{#if this.canTransitionToChemistry}}
        <button
          class="{{@buttonClass}} btn-primary"
          type="button"
          {{on "click" (fn this.setStatus "chemistry")}}
          disabled={{this.loading}}
        >
          <iconify-icon icon="ph:plus-bold" inline />
          Add to shortlist
        </button>
      {{/if}}
      {{#if this.canTransitionToChosen}}
        <button
          class="{{@buttonClass}} btn-primary"
          type="button"
          {{on "click" (fn this.setStatus "chosen")}}
          disabled={{this.loading}}
        >
          <iconify-icon icon="ph:check-bold" inline />
          Choose coach
        </button>
      {{/if}}
      {{#if this.canTransitionToDeclined}}
        <button
          class="{{@buttonClass}} btn-outline-danger"
          type="button"
          {{on "click" (fn this.setStatus "declined")}}
          disabled={{this.loading}}
        >
          <iconify-icon icon="ph:x-bold" inline />
          Reject coach
        </button>
      {{/if}}
    {{/if}}
  </template>
}
